import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Copy, Link2, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';

interface NifLinkItem {
  _id: string;
  token: string;
  status: string;
  expiresAt: string;
  clientLabel: string;
  clientEmailHint: string;
  formUrl: string;
  submittedAt?: string;
  nifSubmission?: { nome?: string; sobrenome?: string; email?: string };
}

const LINK_STATUS: Record<string, string> = {
  awaiting_client: 'Aguardando cliente',
  submitted: 'Enviado',
  expired: 'Expirado',
  revoked: 'Revogado'
};

const AdminNifLinksPanel: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const [items, setItems] = useState<NifLinkItem[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastUrl, setLastUrl] = useState('');
  const [linkForm, setLinkForm] = useState({
    clientLabel: '',
    clientEmailHint: '',
    expiresInDays: 30,
    adminNotes: ''
  });

  const fetchLinks = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await axios.get('/nif/links', { params: { page: 1, limit: 10 } });
      setItems(res.data.items ?? []);
    } catch {
      notifications.error.network();
    } finally {
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const createLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await axios.post('/nif/links', linkForm);
      setLastUrl(res.data.formUrl);
      notifications.showSuccess(t('admin.nifLinkCreated'), t('admin.nifLinkCreatedDesc'));
      setShowForm(false);
      setLinkForm({ clientLabel: '', clientEmailHint: '', expiresInDays: 30, adminNotes: '' });
      fetchLinks();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('admin.nifLinkError');
      notifications.showError(t('common.error'), msg);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    if (!(await confirm({ message: t('admin.nifLinkRevokeConfirm'), confirmText: t('common.revoke'), variant: 'danger' }))) return;
    try {
      await axios.post(`/nif/links/${id}/revoke`);
      fetchLinks();
    } catch {
      notifications.showError(t('common.error'), t('admin.nifLinkRevokeError'));
    }
  };

  const remove = async (id: string) => {
    if (!(await confirm({ message: t('admin.nifLinkDeleteConfirm'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/nif/links/${id}`);
      fetchLinks();
    } catch {
      notifications.showError(t('common.error'), t('admin.nifLinkDeleteError'));
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    notifications.showSuccess(t('admin.nifLinkCopied'), '');
  };

  return (
    <div className="mb-8 bg-white rounded-xl shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('admin.nifLinksTitle')}</h2>
          <p className="text-sm text-gray-600 mt-0.5">{t('admin.nifLinksDesc')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('admin.nifNewLink')}
        </button>
      </div>

      {lastUrl && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex flex-wrap items-center gap-2">
          <Link2 className="w-4 h-4 text-green-700 shrink-0" />
          <code className="text-xs sm:text-sm text-green-900 break-all flex-1">{lastUrl}</code>
          <button
            type="button"
            onClick={() => copyUrl(lastUrl)}
            className="p-2 text-green-800 hover:bg-green-100 rounded-lg transition-colors duration-200"
            title={t('admin.nifLinkCopied')}
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setLastUrl('')}
            className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            title={t('common.close', { defaultValue: 'Fechar' })}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={createLink} className="mb-4 border border-gray-100 rounded-lg p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700">{t('admin.nifLinkClientLabel')}</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={linkForm.clientLabel}
                onChange={(e) => setLinkForm((f) => ({ ...f, clientLabel: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">{t('admin.nifLinkClientEmail')}</span>
              <input
                type="email"
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={linkForm.clientEmailHint}
                onChange={(e) => setLinkForm((f) => ({ ...f, clientEmailHint: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">{t('admin.nifLinkExpiresDays')}</span>
              <input
                type="number"
                min={1}
                max={90}
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={linkForm.expiresInDays}
                onChange={(e) => setLinkForm((f) => ({ ...f, expiresInDays: Number(e.target.value) }))}
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>{t('common.sending', { defaultValue: 'Gerando...' })}</span>
                </>
              ) : (
                <span>{t('admin.nifGenerateLink')}</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={fetchLinks}
          disabled={refreshing}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('admin.refreshList', { defaultValue: 'Atualizar' })}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {initialLoading ? (
        <p className="text-sm text-gray-500 py-4 text-center">{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">{t('admin.nifLinksEmpty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="pb-2 pr-3">{t('admin.nifLinkClientLabel')}</th>
                <th className="pb-2 pr-3">Status</th>
                <th className="pb-2 pr-3">{t('admin.nifLinkExpires')}</th>
                <th className="pb-2">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item._id} className="border-b border-gray-50">
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-900">
                      {item.clientLabel ||
                        (item.nifSubmission
                          ? `${item.nifSubmission.nome || ''} ${item.nifSubmission.sobrenome || ''}`.trim()
                          : '—')}
                    </div>
                    {item.clientEmailHint && (
                      <div className="text-xs text-gray-500">{item.clientEmailHint}</div>
                    )}
                  </td>
                  <td className="py-2 pr-3">{LINK_STATUS[item.status] || item.status}</td>
                  <td className="py-2 pr-3 text-gray-600">
                    {new Date(item.expiresAt).toLocaleDateString()}
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => copyUrl(item.formUrl)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                        title={t('admin.nifLinkCopied')}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {item.status === 'awaiting_client' && (
                        <button
                          type="button"
                          onClick={() => revoke(item._id)}
                          className="p-2 text-amber-700 hover:bg-amber-100 rounded-lg transition-colors duration-200 text-xs"
                        >
                          {t('admin.nifLinkRevoke')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(item._id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminNifLinksPanel;
